import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Send, History, Settings, Loader2, Crown, AlertCircle, BarChart3, MessageSquare, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import FlagToggle from '../components/FlagToggle';
import VoiceRecorderButton from '../components/VoiceRecorderButton';
import VoiceTranscriptPreview from '../components/VoiceTranscriptPreview';
import MessageBubble from '../components/MessageBubble';
import EmptyState from '../components/EmptyState';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { copyToClipboard, downloadAsTxt, downloadAsPdf, downloadAsDocx, generateFilename } from '../utils/downloadUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FORBIDDEN_STATUS = 403;
const DAILY_PROMPT_LIMIT = 10;

const MainApp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'general';
  const conversationId = searchParams.get('conversation');
  
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const [usageInfo, setUsageInfo] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const [autoplayVoice, setAutoplayVoice] = useState(() => {
    try {
      return localStorage.getItem('hussar_voice_autoplay') === '1';
    } catch {
      return false;
    }
  });
  // Increment whenever a new assistant message is added — used to trigger autoplay on only the latest.
  const [latestAssistantStamp, setLatestAssistantStamp] = useState(0);
  const [voiceTranscript, setVoiceTranscript] = useState(null);

  const suggestedPrompts = t('examples.items', { returnObjects: true });

  const isPremium = user?.subscription_status === 'premium';

  // Fetch usage info on component mount
  useEffect(() => {
    const fetchUsageInfo = async () => {
      if (!isPremium) {
        try {
          const response = await axios.get(`${API}/payments/subscription/status`, {
            withCredentials: true
          });
          if (response.data.usage) {
            setUsageInfo({
              used: response.data.usage.prompts_used,
              limit: DAILY_PROMPT_LIMIT
            });
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Failed to fetch usage info:', error);
          }
        }
      }
    };
    
    fetchUsageInfo();
  }, [isPremium]);

  // Load conversation if resuming
  const loadConversation = useCallback(async (id) => {
    try {
      const response = await axios.get(`${API}/conversations/${id}`, {
        withCredentials: true
      });
      
      setMessages(response.data.messages || []);
      setCurrentConversationId(id);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load conversation:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (conversationId || location.state?.conversation) {
      loadConversation(conversationId || location.state.conversation.id);
    }
  }, [conversationId, location.state, loadConversation]);

  const saveConversation = useCallback(async (updatedMessages) => {
    try {
      if (currentConversationId) {
        // Update existing conversation
        await axios.put(`${API}/conversations/${currentConversationId}`, {
          messages: updatedMessages
        }, { withCredentials: true });
      } else {
        // Create new conversation
        const response = await axios.post(`${API}/conversations/`, {
          mode: mode,
          messages: updatedMessages
        }, { withCredentials: true });
        
        setCurrentConversationId(response.data.id);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save conversation:', error);
      }
    }
  }, [currentConversationId, mode]);


  // Download handlers
  const handleCopy = useCallback(async (text, index) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedMessageIndex(index);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    }
  }, []);

  // Voice handlers
  const uploadVoiceRecording = useCallback(async (blob) => {
    try {
      const form = new FormData();
      const ext = (blob.type && blob.type.includes('mp4')) ? 'mp4' : 'webm';
      form.append('audio', blob, `recording.${ext}`);
      form.append('language', i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'pl');
      const res = await axios.post(`${API}/voice/stt`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    } catch (err) {
      // Re-throw with the original response so VoiceRecorderButton can inspect detail.error
      throw err;
    }
  }, [i18n.resolvedLanguage]);

  const fetchTtsAudio = useCallback(async (text, language) => {
    const res = await axios.post(
      `${API}/voice/tts`,
      { text, language },
      { withCredentials: true, responseType: 'blob' }
    );
    return res.data;
  }, []);

  const handleDownloadTxt = useCallback((text) => {
    const filename = generateFilename(text, 'txt', mode);
    downloadAsTxt(text, filename);
  }, [mode]);

  const handleDownloadPdf = useCallback((text) => {
    const filename = generateFilename(text, 'pdf', mode);
    const includeBranding = !isPremium; // Premium users: no branding
    downloadAsPdf(text, filename, includeBranding);
  }, [mode, isPremium]);

  const handleDownloadDocx = useCallback(async (text) => {
    const filename = generateFilename(text, 'docx', mode);
    const includeBranding = !isPremium; // Premium users: no branding
    await downloadAsDocx(text, filename, includeBranding);
  }, [mode, isPremium]);

  const handleSubmit = async (customPrompt = null, inputType = 'text') => {
    const currentPrompt = customPrompt || prompt;
    if (!currentPrompt.trim()) return;

    const userMessage = {
      role: 'user',
      content: currentPrompt,
      input_type: inputType,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setPrompt('');
    setLoading(true);
    setShowUpgradePrompt(false);

    try {
      const response = await axios.post(
        `${API}/ai/generate`,
        {
          prompt: currentPrompt,
          mode: mode,
          session_id: sessionId,
          language: i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'pl',
          input_type: inputType
        },
        { withCredentials: true }
      );

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        input_type: 'text',
        tts_language: response.data.tts_language || (i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'pl'),
        timestamp: new Date()
      };
      
      const updatedMessages = [...messages, userMessage, aiMessage];
      // Stamp a stable client-side id on each new assistant message for telemetry
      const aiIndex = updatedMessages.length - 1;
      updatedMessages[aiIndex] = {
        ...updatedMessages[aiIndex],
        message_id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      };
      setMessages(updatedMessages);
      // Bump stamp so the latest assistant bubble triggers autoplay (if enabled)
      setLatestAssistantStamp(Date.now());
      
      // Save conversation automatically
      await saveConversation(updatedMessages);
      
      // Update usage info
      if (response.data.prompts_used !== undefined) {
        setUsageInfo({
          used: response.data.prompts_used,
          limit: DAILY_PROMPT_LIMIT
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('AI Error:', error);
      }
      
      // Handle usage limit exceeded
      if (error.response?.status === FORBIDDEN_STATUS) {
        const errorData = error.response.data.detail;
        if (errorData?.error === 'usage_limit_reached') {
          setShowUpgradePrompt(true);
          setUsageInfo({
            used: errorData.prompts_used,
            limit: errorData.limit
          });
        }
      } else {
        const errorMessage = {
          role: 'assistant',
          content: t('app.errorGeneric'),
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="hussar-app">
      <Toaster theme="dark" position="top-center" />
      {/* Header */}
      <header className="hussar-app-header">
        <div className="hussar-app-header-content">
          <div className="hussar-logo-full" onClick={() => navigate('/')}>
            <img src="/hussarai-logo-full.png" alt="HussarAi" className="hussar-logo-full-img-small" />
          </div>
          <div className="hussar-app-nav">
            {/* Post-Signup Immigration Activation Fix (2026-02-20) —
                in-app quick links so a new Basic user can reach the
                immigration flow in one click from anywhere on /app. */}
            <a
              href="/mos-precheck"
              className="hussar-app-nav-link"
              data-testid="app-nav-mos-precheck"
            >
              {t('app.intro.ctaPrimary')}
            </a>
            <a
              href="/moje-akta-imigracyjne"
              className="hussar-app-nav-link"
              data-testid="app-nav-moje-akta"
            >
              {t('app.intro.ctaSecondary')}
            </a>
            {isPremium && (
              <div className="hussar-premium-badge">
                <Crown className="w-4 h-4" />
                <span>Premium</span>
              </div>
            )}
            {!isPremium && usageInfo && (
              <div className="hussar-usage-badge">
                <span>{usageInfo.used}/{usageInfo.limit}</span>
              </div>
            )}
            <Button 
              variant="ghost" 
              className="hussar-btn-icon"
              onClick={() => navigate('/conversations')}
              title={t('app.history')}
            >
              <MessageSquare className="w-5 h-5" />
            </Button>
            {user?.role === 'admin' && (
              <Button 
                variant="ghost" 
                className="hussar-btn-icon"
                onClick={() => navigate('/analytics')}
                title={t('app.analytics')}
                data-testid="analytics-nav-btn"
              >
                <BarChart3 className="w-5 h-5" />
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button 
                variant="ghost" 
                className="hussar-btn-icon"
                onClick={() => navigate('/admin/users')}
                title={t('app.users')}
                data-testid="admin-users-nav-btn"
              >
                <Users className="w-5 h-5" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              className="hussar-btn-icon"
              onClick={() => navigate('/subscription')}
            >
              <Settings className="w-5 h-5" />
              <span className="hussar-btn-label">{t('app.subscription')}</span>
            </Button>
            <Button 
              variant="ghost" 
              className="hussar-btn-icon"
              onClick={logout}
            >
              <span className="hussar-btn-label">{t('app.logout')}</span>
            </Button>
            <FlagToggle className="hussar-flag-toggle-header" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="hussar-app-main">
        <div className="hussar-app-container">
          {messages.length === 0 ? (
            <EmptyState
              isPremium={isPremium}
              promptsUsed={usageInfo ? usageInfo.used : 0}
              suggestedPrompts={suggestedPrompts}
              onPromptClick={handleSubmit}
            />
          ) : (
            <>
              {/* Messages */}
              <div className="hussar-messages">
                {messages.map((message, index) => {
                  const isLatestAssistant =
                    message.role === 'assistant' && index === messages.length - 1;
                  return (
                    <MessageBubble
                      key={message.message_id || `${message.timestamp}-${index}`}
                      message={message}
                      index={index}
                      isCopied={copiedMessageIndex === index}
                      language={i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'pl'}
                      ttsFetcher={fetchTtsAudio}
                      autoplay={autoplayVoice && isLatestAssistant}
                      autoplayStamp={latestAssistantStamp}
                      onCopy={handleCopy}
                      onDownloadTxt={handleDownloadTxt}
                      onDownloadPdf={handleDownloadPdf}
                      onDownloadDocx={handleDownloadDocx}
                    />
                  );
                })}
                {loading && (
                  <div className="hussar-message hussar-message-assistant">
                    <div className="hussar-message-content hussar-loading">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t('app.working')}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Upgrade Prompt if limit reached */}
              {showUpgradePrompt && (
                <Card className="hussar-upgrade-prompt">
                  <AlertCircle className="w-6 h-6" style={{ color: '#00D4FF' }} />
                  <div className="hussar-upgrade-prompt-content">
                    <h3 className="hussar-upgrade-prompt-title">{t('app.upgradeLimitTitle')}</h3>
                    <p className="hussar-upgrade-prompt-text">
                      {t('app.upgradeLimitText')}
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate('/subscription')}
                    className="hussar-btn-primary"
                  >
                    <Crown className="w-4 h-4" />
                    {t('app.upgradeUnlock')}
                  </Button>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Input Area */}
      <div className="hussar-input-section">
        <div className="hussar-input-container">
          {voiceTranscript !== null && (
            <VoiceTranscriptPreview
              transcript={voiceTranscript}
              onSend={(text) => {
                setVoiceTranscript(null);
                if (text) handleSubmit(text, 'voice');
              }}
              onCancel={() => setVoiceTranscript(null)}
            />
          )}
          <button
            type="button"
            onClick={() => {
              const next = !autoplayVoice;
              setAutoplayVoice(next);
              try {
                localStorage.setItem('hussar_voice_autoplay', next ? '1' : '0');
              } catch {
                /* noop */
              }
              toast.success(next ? t('app.autoplayOn') : t('app.autoplayOff'));
            }}
            className={`hussar-autoplay-toggle ${autoplayVoice ? 'hussar-autoplay-toggle-on' : ''}`}
            data-testid="autoplay-toggle"
            title={t('app.autoplayHint')}
          >
            <span className="hussar-autoplay-dot" aria-hidden="true" />
            <span>{autoplayVoice ? t('app.autoplayOn') : t('app.autoplayOff')}</span>
          </button>
          <Card className="hussar-input-card">
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('app.placeholder')}
              className="hussar-textarea"
              rows={3}
              disabled={showUpgradePrompt}
            />
            <div className="hussar-input-actions">
              <VoiceRecorderButton
                disabled={loading || showUpgradePrompt}
                uploader={uploadVoiceRecording}
                onTranscript={({ transcript }) => setVoiceTranscript(transcript)}
                onError={(code, msg) => {
                  if (code === 'daily_limit') {
                    setShowUpgradePrompt(true);
                  }
                  toast.error(msg);
                }}
              />
              <Button 
                onClick={() => handleSubmit()}
                disabled={!prompt.trim() || loading || showUpgradePrompt}
                className="hussar-btn-primary"
              >
                <span>{t('app.send')}</span>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MainApp;
